import React, { useCallback, useState, useEffect } from 'react';
import { fileToBase64 } from '../utils/fileHelpers';

interface UploadZoneProps {
  onImageSelected: (base64: string, mimeType: string) => void;
  onError: (message: string) => void;
  isAnalyzing: boolean;
}

const UploadZone: React.FC<UploadZoneProps> = ({ onImageSelected, onError, isAnalyzing }) => {
  const [isDragging, setIsDragging] = useState(false);

  // Paste Event Listener
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (isAnalyzing) return;
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            try {
              const base64 = await fileToBase64(file);
              onImageSelected(base64, file.type);
            } catch (err) {
              console.error("Paste error", err);
              onError("Failed to read image from clipboard.");
            }
            return; // Stop after first image
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [onImageSelected, onError, isAnalyzing]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        const base64 = await fileToBase64(file);
        onImageSelected(base64, file.type);
      } catch (e) {
        console.error("File read error", e);
        onError("Failed to read selected file.");
      }
    }
  }, [onImageSelected, onError]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const base64 = await fileToBase64(file);
        onImageSelected(base64, file.type);
      } catch (error) {
        console.error("Drop error", error);
        onError("Failed to process dropped image.");
      }
    } else if (file) {
        onError("Please drop an image file (PNG or JPG).");
    }
  }, [onImageSelected, onError]);

  const handleScreenCapture = async () => {
    if (isAnalyzing) return;
    try {
        // Use standard browser API to capture screen/window/tab
        const stream = await navigator.mediaDevices.getDisplayMedia({
            video: {
                displaySurface: 'browser', // Hint to prefer tab sharing
            },
            audio: false
        });

        const video = document.createElement('video');
        video.srcObject = stream;
        video.play();

        // Wait for the video to actually start playing to get dimensions
        await new Promise<void>((resolve) => {
            video.onloadedmetadata = () => {
                resolve();
            };
        });

        // Small delay to ensure the frame is fully rendered (avoids black/empty frames)
        await new Promise(r => setTimeout(r, 500));

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const base64 = canvas.toDataURL('image/png');
            onImageSelected(base64, 'image/png');
        }

        // Stop sharing immediately after capture
        stream.getTracks().forEach(track => track.stop());

    } catch (err: any) {
        console.error("Screen capture error:", err);
        if (err.name === 'NotAllowedError' || err.message.includes('denied') || err.message.includes('permissions policy')) {
            onError("Screen capture was blocked by browser permissions. Please use the 'Paste' method below instead.");
        } else {
            onError("Failed to capture screen. Please try uploading a file or pasting.");
        }
    }
  };

  return (
    <div className="flex flex-col gap-4">
        {/* Drag & Drop Area */}
        <div
            className={`
                relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300
                ${isDragging ? 'border-emerald-500 bg-emerald-500/10' : 'border-slate-600 hover:border-emerald-500/50 hover:bg-slate-800/50'}
                ${isAnalyzing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <input
                type="file"
                accept="image/*"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onChange={handleFileChange}
                disabled={isAnalyzing}
            />
            <div className="flex flex-col items-center justify-center space-y-4">
                <div className={`p-4 rounded-full ${isDragging ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                </div>
                <div>
                <p className="text-lg font-medium text-slate-200">
                    Drag & Drop Chart Image
                </p>
                <p className="text-sm text-slate-500 mt-1">
                    or click to browse (JPG, PNG)
                </p>
                </div>
            </div>
        </div>

        {/* TradingView specific tip */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-3 flex items-start gap-3">
            <div className="p-1.5 bg-blue-500/10 rounded-lg text-blue-400 mt-0.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </div>
            <div className="text-sm text-slate-400 leading-relaxed">
                <span className="text-slate-200 font-semibold block mb-1">TradingView Users</span>
                Click the <span className="text-white bg-slate-700 px-1.5 py-0.5 rounded text-xs font-mono">Camera Icon</span> (top-right of chart) → Select <span className="text-white font-medium">Copy Image</span> → Press <span className="text-white bg-slate-700 px-1.5 py-0.5 rounded text-xs font-mono">Ctrl + V</span> here.
            </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 text-slate-600">
            <div className="h-px bg-slate-800 flex-1"></div>
            <span className="text-xs font-medium uppercase tracking-wider">Alternative Methods</span>
            <div className="h-px bg-slate-800 flex-1"></div>
        </div>

        {/* Secondary Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             {/* Paste Info */}
             <div className="flex items-center justify-center p-4 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 select-none group transition-colors hover:border-slate-700">
                 <svg className="w-5 h-5 mr-2 group-hover:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                 </svg>
                 <span className="text-sm">Paste from Clipboard <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 text-xs font-mono border border-slate-700">Ctrl+V</span></span>
             </div>

             {/* Screen Capture Button */}
             <button 
                onClick={handleScreenCapture}
                disabled={isAnalyzing}
                className="flex items-center justify-center p-4 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 hover:border-emerald-500/50 transition-all duration-200 group"
                title="May require browser permissions"
             >
                 <svg className="w-5 h-5 mr-2 text-emerald-500 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                 </svg>
                 <span className="text-sm font-medium">Live Tab Capture</span>
             </button>
        </div>
    </div>
  );
};

export default UploadZone;